import { useState, useEffect } from 'react';
import { api, resolveImageUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Upload, Trash2, GripVertical, Image as ImageIcon } from 'lucide-react';
import { AdminEmptyState, AdminLoadingState, AdminPageHeader, AdminSurface } from '@/components/admin/AdminPrimitives';

export default function AdminGallery() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState('');

  const fetchImages = async () => {
    try {
      const res = await api.get('/admin/gallery');
      setImages(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load gallery');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchImages(); }, []);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      await Promise.all(Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('caption', caption);
        await api.post('/admin/gallery', formData);
      }));
      toast.success(`${files.length} image(s) uploaded`);
      setCaption('');
      fetchImages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success('Image deleted');
      fetchImages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleCaptionUpdate = async (id, newCaption) => {
    try {
      await api.put(`/admin/gallery/${id}`, { caption: newCaption });
      toast.success('Caption updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update caption');
    }
  };

  const moveImage = async (index, direction) => {
    const newImages = [...images];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newImages.length) return;
    [newImages[index], newImages[targetIndex]] = [newImages[targetIndex], newImages[index]];
    setImages(newImages);
    try {
      await api.put('/admin/gallery/reorder', { order: newImages.map(img => img.id) });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reorder');
      fetchImages();
    }
  };

  if (loading) {
    return <AdminLoadingState label="Loading gallery" />;
  }

  return (
    <div data-testid="admin-gallery-page">
      <AdminPageHeader
        eyebrow="Media"
        title="Gallery Manager"
        description="Upload, manage, and reorder ministry gallery images."
      />

      {/* Upload Area */}
      <AdminSurface className="p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-[#0F172A] mb-1">Caption (optional)</label>
            <Input
              data-testid="gallery-caption-input"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Enter image caption"
              className="rounded-xl"
            />
          </div>
          <div>
            <label
              data-testid="gallery-upload-btn"
              className={`inline-flex min-h-10 cursor-pointer items-center justify-center gap-2 rounded-full bg-[#2D5BE3] px-6 py-2.5 font-semibold text-white shadow-[0_12px_28px_rgba(30,79,161,0.22)] transition-all hover:-translate-y-0.5 hover:bg-[#1E4FA1] ${uploading ? 'pointer-events-none opacity-60' : ''}`}
            >
              <Upload size={16} />
              {uploading ? 'Uploading...' : 'Upload Images'}
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>
          </div>
        </div>
      </AdminSurface>

      {/* Images Grid */}
      {images.length === 0 ? (
        <AdminEmptyState icon={ImageIcon} title="No gallery images yet" description="Upload a few ministry moments to start building the public gallery." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {images.map((img, i) => (
            <div key={img.id} data-testid={`gallery-admin-item-${i}`}
              className="mscm-admin-surface overflow-hidden">
              <div className="aspect-square relative">
                <img
                  src={resolveImageUrl(img.thumbnail_path || img.storage_path)}
                  alt={img.caption || 'Gallery'}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 space-y-3">
                <Input
                  value={img.caption || ''}
                  onChange={(e) => {
                    const newImgs = [...images];
                    newImgs[i] = { ...newImgs[i], caption: e.target.value };
                    setImages(newImgs);
                  }}
                  onBlur={() => handleCaptionUpdate(img.id, img.caption || '')}
                  placeholder="Caption"
                  className="rounded-lg text-sm"
                />
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={() => moveImage(i, -1)} disabled={i === 0} className="h-8 w-8 p-0" aria-label="Move image earlier">
                      <GripVertical size={14} className="rotate-90" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => moveImage(i, 1)} disabled={i === images.length - 1} className="h-8 w-8 p-0" aria-label="Move image later">
                      <GripVertical size={14} className="-rotate-90" />
                    </Button>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(img.id)} className="text-red-500 hover:bg-red-50" aria-label="Delete image">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
